import { Link } from "react-router-dom";
import { Bot, Brain, Network, Mic, Building2, Cog, Cpu, MessageSquare, Users, ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "../components/ui/button";

export default function SkillsPage() {
  return (
    <div className="pt-20" data-testid="skills-page">
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="text-xs font-semibold tracking-widest uppercase text-royal-blue-800 mb-4 block">Expertise</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">Skills & Technical Expertise</h1>
            <p className="text-lg text-zinc-600 leading-relaxed">A comprehensive toolkit built over two decades of experience.</p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="skills-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Bot size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Agentic AI Architecture</h3>
              <p className="text-zinc-600 mb-4">Designing autonomous AI systems that reason and execute.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">LangChain</span>
                <span className="tag-badge">AutoGPT</span>
                <span className="tag-badge">Multi-Agent</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Brain size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Generative AI Strategy</h3>
              <p className="text-zinc-600 mb-4">Developing roadmaps for Gen AI adoption.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">GPT-4</span>
                <span className="tag-badge">Claude</span>
                <span className="tag-badge">Gemini</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Network size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">LLM/RAG System Design</h3>
              <p className="text-zinc-600 mb-4">Building production-grade retrieval augmented generation.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">Pinecone</span>
                <span className="tag-badge">ChromaDB</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Mic size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Voice AI & Routing</h3>
              <p className="text-zinc-600 mb-4">Implementing voice automation for contact centers.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">Speech Recognition</span>
                <span className="tag-badge">NLU</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Building2 size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Contact Center Modernization</h3>
              <p className="text-zinc-600 mb-4">Transforming legacy contact centers with AI.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">CCaaS</span>
                <span className="tag-badge">Agent Assist</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Cog size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">ML Automation</h3>
              <p className="text-zinc-600 mb-4">Building scalable ML pipelines and MLOps.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">MLflow</span>
                <span className="tag-badge">Kubeflow</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Cpu size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Enterprise AI Integration</h3>
              <p className="text-zinc-600 mb-4">Integrating AI solutions with enterprise systems.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">API Design</span>
                <span className="tag-badge">Microservices</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><MessageSquare size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Technical Troubleshooting</h3>
              <p className="text-zinc-600 mb-4">Deep debugging of AI systems in production.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">Performance</span>
                <span className="tag-badge">Root Cause</span>
              </div>
            </div>
            <div className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div className="skill-icon mb-6"><Users size={24} /></div>
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">Executive Communication</h3>
              <p className="text-zinc-600 mb-4">Translating AI concepts for business stakeholders.</p>
              <div className="flex flex-wrap gap-2">
                <span className="tag-badge">Workshops</span>
                <span className="tag-badge">Training</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 section-muted" data-testid="tech-stack-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-royal-blue-800 mb-4 block">Technologies</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">Technical Stack</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="p-6 bg-white border border-zinc-200 rounded-sm">
              <h3 className="font-semibold text-royal-blue-800 mb-4 text-sm uppercase tracking-wider">Languages</h3>
              <ul className="space-y-2 text-zinc-700 text-sm"><li>Python</li><li>JavaScript</li><li>SQL</li><li>Bash</li></ul>
            </div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm">
              <h3 className="font-semibold text-royal-blue-800 mb-4 text-sm uppercase tracking-wider">AI/ML</h3>
              <ul className="space-y-2 text-zinc-700 text-sm"><li>PyTorch</li><li>TensorFlow</li><li>LangChain</li><li>OpenAI</li></ul>
            </div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm">
              <h3 className="font-semibold text-royal-blue-800 mb-4 text-sm uppercase tracking-wider">Cloud</h3>
              <ul className="space-y-2 text-zinc-700 text-sm"><li>AWS</li><li>Azure</li><li>GCP</li></ul>
            </div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm">
              <h3 className="font-semibold text-royal-blue-800 mb-4 text-sm uppercase tracking-wider">Databases</h3>
              <ul className="space-y-2 text-zinc-700 text-sm"><li>PostgreSQL</li><li>MongoDB</li><li>Neo4j</li><li>Pinecone</li></ul>
            </div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm">
              <h3 className="font-semibold text-royal-blue-800 mb-4 text-sm uppercase tracking-wider">DevOps</h3>
              <ul className="space-y-2 text-zinc-700 text-sm"><li>Docker</li><li>Kubernetes</li><li>Terraform</li><li>GitHub</li></ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase text-royal-blue-800 mb-4 block">Approach</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-6">Pragmatic AI Implementation</h2>
              <p className="text-lg text-zinc-600 leading-relaxed mb-6">I believe in building AI solutions that work in the real world.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><Lightbulb className="text-royal-blue-800 flex-shrink-0 mt-1" size={20} /><span className="text-zinc-700"><strong className="text-zinc-950">Business-First Design:</strong> Starting with business outcomes</span></li>
                <li className="flex items-start gap-3"><Lightbulb className="text-royal-blue-800 flex-shrink-0 mt-1" size={20} /><span className="text-zinc-700"><strong className="text-zinc-950">Production-Ready:</strong> Building for scale and reliability</span></li>
                <li className="flex items-start gap-3"><Lightbulb className="text-royal-blue-800 flex-shrink-0 mt-1" size={20} /><span className="text-zinc-700"><strong className="text-zinc-950">Measurable Impact:</strong> Defining clear KPIs</span></li>
              </ul>
            </div>
            <div className="bg-zinc-950 text-white p-8 md:p-12 rounded-sm">
              <h3 className="font-display text-2xl font-semibold mb-6">Let's Build Together</h3>
              <p className="text-zinc-400 mb-8">Whether you need help with an AI challenge or want a comprehensive strategy.</p>
              <Link to="/contact">
                <Button className="bg-white text-zinc-950 hover:bg-zinc-100 px-8 py-6 text-xs tracking-wider uppercase" data-testid="skills-contact-btn">Start a Conversation <ArrowRight className="ml-2" size={16} /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
